/* ==========================================================================
   scuffers-card.js — B4 shared product card behavior (Scuffers 1:1)
   - Image-nav arrows ([data-card-arrow]) slide the gallery track through all
     images (translateX, 0.3s ease, clamp at the ends — no wrap, per source
     swiper cards). .is-sliding + [data-gallery-index] on the card wrapper;
     back at image 1 the class is dropped so the hover crossfade resumes.
   - Quick-add "+" opens the frosted size strip (.product-item__form.is-open)
   - Clicking a size POSTs /cart/add.js, refreshes /cart.js, updates
     [data-cart-count], dispatches 'cart:refresh' (detail = cart JSON) and
     opens the cart drawer via window.SadyCart?.open?.() with fallbacks to
     [data-cart-toggle] / #cart-drawer.
   - Idempotent: guarded by window.__scuffersCardInit and implemented as ONE
     delegated document listener, so the snippet's per-card <script> fallback
     can load this file multiple times safely.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__scuffersCardInit) return;
  window.__scuffersCardInit = true;

  var ROUTES = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function closeForm(form) {
    if (!form) return;
    form.classList.remove('is-open');
    var card = form.closest('[data-product-card]');
    if (card) card.classList.remove('quick-add-open');
    var trigger = card && card.querySelector('[data-quick-add]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  function closeAllForms(except) {
    document.querySelectorAll('[data-quick-add-form].is-open').forEach(function (f) {
      if (f !== except) closeForm(f);
    });
  }

  function openDrawer() {
    if (window.SadyCart && typeof window.SadyCart.open === 'function') {
      window.SadyCart.open();
      return;
    }
    var toggle = document.querySelector('[data-cart-toggle]');
    if (toggle) {
      toggle.click();
      return;
    }
    var drawer = document.getElementById('cart-drawer');
    if (drawer) {
      drawer.classList.add('is-open');
      document.body.classList.add('no-scroll');
    }
  }

  function updateCartCount(cart) {
    if (!cart) return;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
    });
  }

  function addToCart(variantId, btn) {
    if (!variantId) return Promise.resolve();
    if (btn) btn.classList.add('is-loading');
    return fetch(ROUTES + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: 1 })
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (err) { throw err; });
        return res.json();
      })
      .then(function (item) {
        return fetch(ROUTES + 'cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (res) { return res.json(); })
          .then(function (cart) {
            updateCartCount(cart);
            document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart: cart, added: item } }));
            /* QA 2026-07-03: per collection-product-card.spec.md the live
               quick-add is SILENT — no cart drawer opens. openDrawer() removed. */
          });
      })
      .catch(function (err) {
        /* eslint-disable-next-line no-console */
        console.warn('[scuffers-card] add to cart failed', err);
      })
      .then(function () {
        if (btn) btn.classList.remove('is-loading');
      });
  }

  function slideGallery(arrow) {
    var card = arrow.closest('[data-product-card]');
    var track = card && card.querySelector('.clean-gallery-track');
    if (!track) return;
    var count = track.querySelectorAll('.clean-gallery-item').length;
    if (count < 2) return;
    var idx;
    if (!card.classList.contains('is-sliding')) {
      /* Entering from the crossfade rest state: the hovered card is already
         showing image 2, so sliding starts at index 1 — jump there instantly
         (no transition), then animate to the target. Seamless takeover. */
      idx = 1;
      card.classList.add('is-sliding');
      track.style.transition = 'none';
      track.style.transform = 'translateX(-100%)';
      void track.offsetWidth; /* flush so the jump doesn't animate */
      track.style.transition = '';
    } else {
      idx = parseInt(card.getAttribute('data-gallery-index') || '1', 10) || 0;
    }
    idx += arrow.getAttribute('data-card-arrow') === 'next' ? 1 : -1;
    if (idx < 0) idx = 0;
    if (idx > count - 1) idx = count - 1;
    card.setAttribute('data-gallery-index', String(idx));
    track.style.transform = 'translateX(' + (-100 * idx) + '%)';
    var prevBtn = card.querySelector('[data-card-arrow="prev"]');
    var nextBtn = card.querySelector('[data-card-arrow="next"]');
    if (prevBtn) prevBtn.disabled = idx === 0;
    if (nextBtn) nextBtn.disabled = idx === count - 1;
    if (idx === 0) {
      /* Back at image 1: restore crossfade mode, but only once the pointer
         leaves the card — dropping .is-sliding while still hovered would
         flash the crossfade (image 2) over the image the user navigated to.
         Buttons reset to the rest state (hover shows image 2 => prev active,
         next disabled only for 2-image products). */
      var cleanup = function () {
        card.removeEventListener('mouseleave', cleanup);
        if (card.getAttribute('data-gallery-index') !== '0') return;
        card.classList.remove('is-sliding');
        track.style.transform = '';
        if (prevBtn) prevBtn.disabled = false;
        if (nextBtn) nextBtn.disabled = count === 2;
      };
      card.addEventListener('mouseleave', cleanup);
    }
  }

  document.addEventListener('click', function (e) {
    /* image-nav arrows (live inside the media <a> — must not navigate) */
    var arrow = e.target.closest('[data-card-arrow]');
    if (arrow) {
      e.preventDefault();
      e.stopPropagation();
      slideGallery(arrow);
      return;
    }

    /* "+" trigger (lives inside the media <a> — must not navigate) */
    var plus = e.target.closest('[data-quick-add]');
    if (plus) {
      e.preventDefault();
      e.stopPropagation();
      var card = plus.closest('[data-product-card]');
      var form = card && card.querySelector('[data-quick-add-form]');
      if (form) {
        closeAllForms(form);
        form.classList.add('is-open');
        card.classList.add('quick-add-open');
        plus.setAttribute('aria-expanded', 'true');
      } else if (plus.getAttribute('data-qa-variant')) {
        /* single-variant product: add directly (attr renamed so theme.js's
           own [data-quick-add] handler — which opens the drawer — no-ops) */
        addToCart(plus.getAttribute('data-qa-variant'), plus);
      }
      return;
    }

    /* ✕ close */
    var close = e.target.closest('[data-quick-add-close]');
    if (close) {
      e.preventDefault();
      e.stopPropagation();
      closeForm(close.closest('[data-quick-add-form]'));
      return;
    }

    /* size button -> AJAX add */
    var sizeBtn = e.target.closest('.product-item__quick_add_button--variant');
    if (sizeBtn) {
      e.preventDefault();
      e.stopPropagation();
      if (sizeBtn.disabled || sizeBtn.classList.contains('is-loading')) return;
      addToCart(sizeBtn.getAttribute('data-variant-id'), sizeBtn).then(function () {
        closeForm(sizeBtn.closest('[data-quick-add-form]'));
      });
      return;
    }

    /* click anywhere else (incl. inside strip padding): close open strips */
    if (!e.target.closest('[data-quick-add-form]')) closeAllForms();
  });

  /* keyboard: Enter/Space on the "+" (it's a div role=button), Esc closes */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      closeAllForms();
      return;
    }
    if ((e.key === 'Enter' || e.key === ' ') && e.target.closest && e.target.closest('[data-quick-add]')) {
      e.preventDefault();
      e.target.closest('[data-quick-add]').click();
    }
  });
})();
